import { useState, useCallback, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { backendAPI } from '../api/backend'
import './StylistChat.css'

/**
 * StylistChat Component
 *
 * Interactive AI stylist chat with Leo.
 * Features:
 * - Real-time chat with AI stylist
 * - In-chat product cards (3-column grid)
 * - Token usage progress bar
 * - Drag-and-drop image upload with compression
 * - Authentication gate
 */
const StylistChat = () => {
  const { currentUser } = useAuth()
  const isAuthenticated = !!currentUser  // Derive from currentUser
  const location = useLocation()
  const navigate = useNavigate()
  const [messages, setMessages] = useState([])
  const [inputText, setInputText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [conversationId, setConversationId] = useState(null)
  const [tokenUsage, setTokenUsage] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [pendingImage, setPendingImage] = useState(null)
  const [error, setError] = useState(null)
  const [outOfCredits, setOutOfCredits] = useState(false)
  const [claimingCredits, setClaimingCredits] = useState(false)
  const [initialMessageSent, setInitialMessageSent] = useState(false)
  const [clickedProducts, setClickedProducts] = useState([]) // Track products user clicked

  const chatEndRef = useRef(null)
  const fileInputRef = useRef(null)

  // Handle product click - track for Leo's context
  const handleProductClick = (product) => {
    // Add to clicked products (keep last 3)
    setClickedProducts(prev => {
      const updated = [...prev, {
        name: product.name,
        brand: product.brand,
        price: product.price,
        clickedAt: new Date().toISOString()
      }]
      return updated.slice(-3) // Keep only last 3 clicks
    })
  }

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Initial greeting from Leo
  useEffect(() => {
    const testModeEnabled = import.meta.env.VITE_ENABLE_TEST_MODE === 'true'
    if ((isAuthenticated || testModeEnabled) && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm Leo, your personal AI stylist. 👋\n\nI'd love to help you find the perfect look. What's the occasion you're styling for?"
      }])
    }
  }, [isAuthenticated, messages.length])

  // Handle initial message from home page
  useEffect(() => {
    const testModeEnabled = import.meta.env.VITE_ENABLE_TEST_MODE === 'true'
    if ((isAuthenticated || testModeEnabled) && location.state?.initialMessage && !initialMessageSent && messages.length > 0) {
      setInputText(location.state.initialMessage)
      setInitialMessageSent(true)

      // Auto-send after a brief delay
      setTimeout(() => {
        const msg = location.state.initialMessage
        setInputText('')

        // Add user message
        setMessages(prev => [...prev, { role: 'user', content: msg }])
        setIsLoading(true)

        // Send to backend
        backendAPI.chatWithStylist(msg, conversationId, null)
          .then(response => {
            if (response.conversation_id) {
              setConversationId(response.conversation_id)
            }
            if (response.token_usage) {
              setTokenUsage(response.token_usage)
            }
            setMessages(prev => [...prev, { role: 'assistant', content: response.response }])
          })
          .catch(err => {
            setError(err.message || 'Failed to send message')
          })
          .finally(() => {
            setIsLoading(false)
          })
      }, 500)
    }
  }, [isAuthenticated, location.state, initialMessageSent, messages.length, conversationId])

  // Parse product cards from AI response
  const parseProductCards = (text) => {
    const products = []
    let cleanText = text

    // Extract products using brace matching
    const cardPattern = /\[PRODUCT_CARD:\s*/g
    let match
    const productCardRanges = [] // Track ranges to remove

    while ((match = cardPattern.exec(text)) !== null) {
      const startOfCard = match.index
      const jsonStart = text.indexOf('{', match.index)
      if (jsonStart === -1) continue
      let depth = 0, jsonEnd = -1
      for (let i = jsonStart; i < text.length; i++) {
        if (text[i] === '{') depth++
        if (text[i] === '}') depth--
        if (depth === 0) { jsonEnd = i; break }
      }
      if (jsonEnd === -1) continue

      // Find the closing bracket
      let endOfCard = jsonEnd + 1
      while (endOfCard < text.length && text[endOfCard] !== ']') {
        if (text[endOfCard] !== ' ' && text[endOfCard] !== '\n') break
        endOfCard++
      }
      if (text[endOfCard] === ']') endOfCard++

      // Track this range for removal
      productCardRanges.push({ start: startOfCard, end: endOfCard })

      try {
        products.push(JSON.parse(text.substring(jsonStart, jsonEnd + 1)))
      } catch (e) {
        console.warn('Failed to parse product card:', e)
      }
    }

    // Remove all found PRODUCT_CARD ranges from end to start (to preserve indices)
    productCardRanges.sort((a, b) => b.start - a.start)
    for (const range of productCardRanges) {
      cleanText = cleanText.substring(0, range.start) + cleanText.substring(range.end)
    }

    // Additional cleanup for any remaining patterns
    cleanText = cleanText
      .replace(/\[PRODUCT_CARD:\s*\{[\s\S]*?\}\s*\]/g, '')
      .replace(/\[PRODUCT_CARD:[^\]]*\]/g, '')
      .replace(/PRODUCT_CARD:\s*\{[^}]*\}/g, '')
      .replace(/\[?PRODUCT_CARD[^\]]*\]?/gi, '')
      .replace(/\{\s*"name"[^}]+\}/g, '') // Remove any orphan JSON objects with "name"
      .replace(/\n\s*\n\s*\n+/g, '\n\n')
      .trim()

    return { cleanText, products }
  }

  // Compress image to max 1MB
  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let { width, height } = img

          // Max dimensions
          const maxDim = 1200
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = (height / width) * maxDim
              width = maxDim
            } else {
              width = (width / height) * maxDim
              height = maxDim
            }
          }

          canvas.width = width
          canvas.height = height

          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)

          // Compress to JPEG with quality adjustment
          let quality = 0.8
          let base64 = canvas.toDataURL('image/jpeg', quality)

          // Reduce quality if still too large (> 1MB)
          while (base64.length > 1024 * 1024 * 1.37 && quality > 0.3) {
            quality -= 0.1
            base64 = canvas.toDataURL('image/jpeg', quality)
          }

          // Remove data URL prefix
          const base64Data = base64.split(',')[1]
          resolve(base64Data)
        }
        img.onerror = reject
        img.src = e.target.result
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  // Handle drop
  const handleDrop = useCallback(async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const file = e.dataTransfer?.files?.[0]
    if (file && file.type.startsWith('image/')) {
      try {
        const compressed = await compressImage(file)
        setPendingImage({
          preview: URL.createObjectURL(file),
          base64: compressed
        })
      } catch (err) {
        setError('Failed to process image')
      }
    }
  }, [])

  // Handle file input change
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      try {
        const compressed = await compressImage(file)
        setPendingImage({
          preview: URL.createObjectURL(file),
          base64: compressed
        })
      } catch (err) {
        setError('Failed to process image')
      }
    }
  }

  // Send message to Leo
  const sendMessage = async () => {
    if (!inputText.trim() && !pendingImage) return

    const testModeEnabled = import.meta.env.VITE_ENABLE_TEST_MODE === 'true'
    if (!isAuthenticated && !testModeEnabled) {
      setError('Please log in to chat with Leo')
      return
    }

    let userMessage = inputText.trim()

    // Append clicked products context for Leo (invisible to user in chat)
    let messageForLeo = userMessage
    if (clickedProducts.length > 0) {
      const clickContext = clickedProducts.map(p =>
        `[USER_CLICKED: ${p.name} by ${p.brand} at ${p.price}]`
      ).join(' ')
      messageForLeo = `${clickContext}\n\n${userMessage}`
      // Clear clicked products after sending
      setClickedProducts([])
    }

    setInputText('')
    setIsLoading(true)
    setError(null)

    // Add user message to chat (show original, not with context)
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      image: pendingImage?.preview
    }
    setMessages(prev => [...prev, newUserMessage])

    // Clear pending image
    const imageBase64 = pendingImage?.base64
    setPendingImage(null)

    try {
      const response = await backendAPI.chatWithStylist(
        messageForLeo, // Send message with product click context
        conversationId,
        imageBase64
      )

      if (response.conversation_id) {
        setConversationId(response.conversation_id)
      }

      if (response.token_usage) {
        setTokenUsage(response.token_usage)
      }

      // Parse response for product cards
      const { cleanText, products } = parseProductCards(response.response || '')

      // Add AI response to chat
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: cleanText,
        products: products.length > 0 ? products : null
      }])

    } catch (err) {
      console.error('Chat error:', err)

      if (err.response?.status === 401) {
        setError('Please log in to continue chatting')
      } else if (err.response?.status === 402) {
        setOutOfCredits(true)
        setError(null)
      } else {
        setError('Failed to send message. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Claim free credits (one-time only)
  const claimFreeCredits = async () => {
    setClaimingCredits(true)
    try {
      const response = await backendAPI.claimFreeCredits()
      if (response.success) {
        setOutOfCredits(false)
        setTokenUsage(prev => ({
          ...prev,
          session_remaining: (prev?.session_remaining || 0) + response.credits_added,
          daily_remaining: (prev?.daily_remaining || 0) + response.credits_added
        }))
        // Show success message
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `🎉 Great news! I've added ${response.credits_added.toLocaleString()} free credits to your account. Let's continue styling!`
        }])
      } else {
        setError(response.message || 'Unable to claim free credits')
        setOutOfCredits(false)
      }
    } catch (err) {
      console.error('Claim credits error:', err)
      if (err.response?.status === 409) {
        setError('You\'ve already claimed your free credits. Premium plans coming soon!')
      } else {
        setError('Failed to claim credits. Please try again.')
      }
      setOutOfCredits(false)
    } finally {
      setClaimingCredits(false)
    }
  }

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // Calculate token progress percentage
  const tokenProgress = tokenUsage ?
    Math.min(100, (tokenUsage.tokens_used_session / tokenUsage.session_limit) * 100) : 0

  // Check if test mode is enabled
  const testModeEnabled = import.meta.env.VITE_ENABLE_TEST_MODE === 'true'

  // Handle login redirect - stores intended action so user returns to chat after login
  const handleLoginClick = () => {
    localStorage.setItem('intendedAction', 'chat')
    localStorage.setItem('intendedActionTimestamp', Date.now().toString())
    navigate('/auth', {
      state: {
        intendedAction: 'chat',
        message: 'Sign in to chat with Leo, your AI stylist'
      }
    })
  }

  // Auth gate - bypass in test mode
  if (!isAuthenticated && !testModeEnabled) {
    return (
      <div className="stylist-chat-auth-gate">
        <div className="auth-gate-content">
          <h2>Meet Leo, Your AI Stylist</h2>
          <p>Log in to get personalized style recommendations from our AI fashion expert.</p>
          <button onClick={handleLoginClick} className="auth-gate-button">Log In to Chat</button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`stylist-chat ${dragActive ? 'drag-active' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {/* Header with token usage */}
      <div className="chat-header">
        <div className="chat-header-info">
          <h3>Chat with Leo</h3>
          <span className="stylist-badge">AI Stylist</span>
        </div>
        {tokenUsage && (
          <div className="token-usage">
            <div className="token-bar">
              <div
                className="token-bar-fill"
                style={{ width: `${100 - tokenProgress}%` }}
              />
            </div>
            <span className="token-text">
              {tokenUsage.session_remaining.toLocaleString()} credits left
            </span>
          </div>
        )}
      </div>

      {/* Chat messages */}
      <div className="chat-messages">
        {messages.map((msg, idx) => (
          <div key={idx} className={`chat-message ${msg.role}`}>
            {msg.role === 'assistant' && (
              <div className="message-avatar">
                <img src="/Leo.png" alt="Leo" />
              </div>
            )}
            <div className="message-content">
              {msg.image && (
                <img src={msg.image} alt="Uploaded" className="message-image" />
              )}
              <p>{msg.content}</p>

              {/* Product cards grid */}
              {msg.products && msg.products.length > 0 && (
                <div className="leo-product-grid">
                  {msg.products.map((product, pIdx) => (
                    <a
                      key={pIdx}
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="leo-product-card"
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="leo-product-image">
                        <img
                          src={product.image}
                          alt={product.name}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/200?text=No+Image'
                          }}
                        />
                        {/* Overlay with brand and price - always visible */}
                        <div className="leo-product-overlay">
                          <span className="leo-product-brand">{product.brand || 'Fashion'}</span>
                          <span className="leo-product-price">{product.price}</span>
                        </div>
                      </div>
                      <div className="leo-product-info">
                        <h4>{product.name}</h4>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="chat-message assistant">
            <div className="message-avatar">
              <img src="/Leo.png" alt="Leo" />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Out of credits overlay */}
      {outOfCredits && (
        <div className="credits-overlay">
          <div className="credits-modal">
            <h3>⚠️ You've used all your credits!</h3>
            <p>You've been chatting a lot with Leo - that's great! But you've run out of credits for now.</p>
            <div className="credits-options">
              <button
                className="claim-credits-btn"
                onClick={claimFreeCredits}
                disabled={claimingCredits}
              >
                {claimingCredits ? 'Claiming...' : '🎁 Get 3,000 Free Credits'}
              </button>
              <p className="credits-note">One-time bonus for our early users!</p>
            </div>
            <button
              className="credits-close"
              onClick={() => setOutOfCredits(false)}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="chat-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Drag overlay */}
      {dragActive && (
        <div className="drag-overlay">
        <div className="drag-content">
            <span>📷</span>
            <p>Drop image to analyze</p>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="chat-input-area">
        <button
          className="image-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          title="Upload image"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
            <circle cx="12" cy="13" r="4"/>
          </svg>
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          style={{ display: 'none' }}
        />
        <div className="textarea-wrapper">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Leo about your style..."
            rows={1}
            disabled={isLoading}
          />
          {/* Pending image thumbnail inside input */}
          {pendingImage && (
            <div className="pending-image-inline">
              <img src={pendingImage.preview} alt="To upload" />
              <button onClick={() => setPendingImage(null)} aria-label="Remove image">×</button>
            </div>
          )}
        </div>
        <button
          className="send-btn"
          onClick={sendMessage}
          disabled={isLoading || (!inputText.trim() && !pendingImage)}
        >
          {isLoading ? '...' : '→'}
        </button>
      </div>
    </div>
  )
}

export default StylistChat


